/**
 * AdminAiSystemMapSubtab — read-only "what powers Ion" overview.
 *
 * Renders GET /admin/ion-overview: summary tiles, the Cell Data Catalogue
 * (searchable metric table by category + curve types with a decomposition
 * badge), the AI tool registry, knowledge counts, active models, and the
 * hidden/proprietary columns. Read-only — editing lives in the YAML/Python
 * (catalogue) and the Knowledge subtab (knowledge).
 */
import { useEffect, useMemo, useState } from 'preact/hooks';
import { authenticatedFetch } from '../../services/authService.js';
import { getApiBase } from './analytics/shared/formatters.js';

const BADGE = {
  single: { label: 'single', color: '#888' },
  c_rate_sweep: { label: 'C-rate sweep', color: '#0d7c99' },
  temperature_sweep: { label: 'temp sweep', color: '#ea5b0c' },
};

const S = {
  tiles: { display: 'flex', flexWrap: 'wrap', gap: '12px', margin: '8px 0 20px' },
  tile: {
    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '8px', padding: '12px 16px', minWidth: '92px',
  },
  tileNum: { fontFamily: 'Big John, sans-serif', fontSize: '22px', color: '#fff' },
  tileLabel: { fontSize: '11px', color: '#9aa', textTransform: 'uppercase', letterSpacing: '0.04em' },
  section: { marginBottom: '22px' },
  h: { fontFamily: 'Big John, sans-serif', fontSize: '14px', color: '#cfd2d6', margin: '0 0 8px' },
  search: {
    background: '#161616', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '6px',
    color: '#eee', padding: '6px 10px', fontSize: '13px', width: '240px', marginRight: '8px',
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  th: {
    textAlign: 'left', color: '#9aa', fontWeight: 600, padding: '4px 8px',
    borderBottom: '1px solid rgba(255,255,255,0.1)', position: 'sticky', top: 0,
  },
  td: { padding: '4px 8px', borderBottom: '1px solid rgba(255,255,255,0.04)', color: '#cfd2d6' },
  mono: { fontFamily: 'monospace', color: '#8ab' },
  catHead: {
    cursor: 'pointer', color: '#ea5b0c', fontWeight: 600, padding: '8px 0 4px', fontSize: '13px',
  },
  badge: (c) => ({
    display: 'inline-block', padding: '1px 7px', borderRadius: '10px', fontSize: '10px',
    color: '#fff', background: c, marginLeft: '8px',
  }),
  pill: {
    display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '11px',
    background: 'rgba(13,124,153,0.18)', color: '#7fd', marginRight: '6px',
  },
  card: {
    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: '8px', padding: '10px 14px', marginBottom: '8px',
  },
  collapseBtn: {
    background: 'none', border: 'none', color: '#cfd2d6', cursor: 'pointer',
    fontFamily: 'Big John, sans-serif', fontSize: '14px', padding: 0,
  },
};

function Tile({ n, label }) {
  return (
    <div style={S.tile}>
      <div style={S.tileNum}>{n}</div>
      <div style={S.tileLabel}>{label}</div>
    </div>
  );
}

function Section({ title, count, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={S.section}>
      <button type="button" style={S.collapseBtn} onClick={() => setOpen(!open)}>
        {open ? '▾' : '▸'} {title}{count != null ? ` (${count})` : ''}
      </button>
      {open && <div style={{ marginTop: '8px' }}>{children}</div>}
    </div>
  );
}

function CatalogueSection({ catalogue }) {
  const [q, setQ] = useState('');
  const [kind, setKind] = useState('all');
  const [openCat, setOpenCat] = useState(() => new Set(['electrical']));

  const needle = q.trim().toLowerCase();
  const filtered = useMemo(() => {
    return catalogue.categories
      .map((c) => ({
        ...c,
        metrics: c.metrics.filter((m) => {
          if (kind !== 'all' && m.kind !== kind) return false;
          if (!needle) return true;
          return (
            m.label.toLowerCase().includes(needle) ||
            m.column.toLowerCase().includes(needle) ||
            (m.aliases || []).some((a) => a.toLowerCase().includes(needle))
          );
        }),
      }))
      .filter((c) => c.metrics.length > 0);
  }, [catalogue, needle, kind]);

  const toggle = (cat) => {
    const next = new Set(openCat);
    next.has(cat) ? next.delete(cat) : next.add(cat);
    setOpenCat(next);
  };
  // When searching, expand everything so matches are visible.
  const isOpen = (cat) => (needle ? true : openCat.has(cat));

  return (
    <Section title="Cell Data Catalogue" defaultOpen>
      <div style={{ marginBottom: '10px' }}>
        <input
          style={S.search}
          placeholder="search metrics…"
          value={q}
          onInput={(e) => setQ(e.target.value)}
        />
        <select
          style={{ ...S.search, width: '120px' }}
          value={kind}
          onChange={(e) => setKind(e.target.value)}
        >
          <option value="all">all kinds</option>
          <option value="scalar">scalar</option>
          <option value="curve">curve</option>
          <option value="categorical">categorical</option>
        </select>
      </div>

      {filtered.map((c) => (
        <div key={c.category}>
          <div style={S.catHead} onClick={() => toggle(c.category)}>
            {isOpen(c.category) ? '▾' : '▸'} {c.category} ({c.metrics.length})
          </div>
          {isOpen(c.category) && (
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>label</th>
                  <th style={S.th}>column</th>
                  <th style={S.th}>unit</th>
                  <th style={S.th}>kind</th>
                  <th style={S.th}>cond.</th>
                  <th style={S.th}>aliases</th>
                </tr>
              </thead>
              <tbody>
                {c.metrics.map((m) => (
                  <tr key={m.column}>
                    <td style={S.td}>{m.label}</td>
                    <td style={{ ...S.td, ...S.mono }}>{m.column}</td>
                    <td style={S.td}>{m.unit || '—'}</td>
                    <td style={S.td}>{m.kind}</td>
                    <td style={S.td}>{m.condition || '—'}</td>
                    <td style={{ ...S.td, color: '#889' }}>{(m.aliases || []).join(', ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ))}

      <div style={{ ...S.h, marginTop: '18px' }}>Curve types</div>
      {catalogue.curve_types.map((ct) => {
        const b = BADGE[ct.decomposition] || BADGE.single;
        return (
          <div key={ct.name} style={S.card}>
            <span style={S.mono}>{ct.name}</span>
            <span style={S.badge(b.color)}>{b.label}</span>
            <div style={{ fontSize: '11px', color: '#9aa', marginTop: '3px' }}>
              {ct.x.label} / {ct.x.unit} → {ct.y.label} / {ct.y.unit}
            </div>
            {ct.decomposition !== 'single' && (
              <div style={{ fontSize: '11px', color: '#7a7', marginTop: '2px' }}>
                ⤷ variants resolve per cell via list_available_curves
              </div>
            )}
          </div>
        );
      })}
    </Section>
  );
}

export function AdminAiSystemMapSubtab() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await authenticatedFetch(`${getApiBase()}/admin/ion-overview`);
        if (cancelled) return;
        if (!r.ok) {
          setError(`HTTP ${r.status}`);
          return;
        }
        setData(await r.json());
      } catch (e) {
        if (!cancelled) setError(String(e));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (error) {
    return <div className="admin-aim-subtab-body" style={{ color: '#e88' }}>Failed to load AI system map: {error}</div>;
  }
  if (!data) {
    return <div className="admin-aim-subtab-body" style={{ color: '#9aa' }}>Loading…</div>;
  }

  const s = data.summary;
  return (
    <div className="admin-aim-subtab-body">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <h3 style={S.h}>Ion System Map</h3>
        <div style={{ fontSize: '11px', color: '#9aa' }}>
          models:{' '}
          {data.models.map((m) => (
            <span key={m.id} style={S.pill}>{m.label} · {m.tiers.join('/')}</span>
          ))}
        </div>
      </div>

      <div style={S.tiles}>
        <Tile n={s.tools} label="tools" />
        <Tile n={s.metrics} label="metrics" />
        <Tile n={s.curve_types} label="curves" />
        <Tile n={`${s.knowledge_active}/${s.knowledge_total}`} label="knowledge" />
        <Tile n={s.models} label="models" />
        <Tile n={s.hidden_columns} label="hidden" />
      </div>

      <CatalogueSection catalogue={data.catalogue} />

      <Section title="AI Tools" count={data.tools.length} defaultOpen={false}>
        {data.tools.map((t) => (
          <div key={t.name} style={S.card}>
            <span style={S.mono}>{t.name}</span>
            <div style={{ fontSize: '12px', color: '#cfd2d6', margin: '4px 0' }}>{t.description}</div>
            <div style={{ fontSize: '11px', color: '#9aa' }}>
              params: {t.params.length === 0 ? '—' :
                t.params.map((p) => `${p.name}${p.required ? '' : '?'}: ${p.type}`).join(' · ')}
            </div>
          </div>
        ))}
      </Section>

      <Section title="Knowledge" count={`${s.knowledge_active} active`} defaultOpen={false}>
        <div style={{ fontSize: '12px', color: '#9aa' }}>
          {s.knowledge_active} active of {s.knowledge_total} total knowledge entries (DB-resident).
          Manage content in the <strong style={{ color: '#cfd2d6' }}>Knowledge</strong> subtab.
        </div>
      </Section>

      <Section title="Hidden / proprietary columns" count={data.hidden_columns.length} defaultOpen={false}>
        {data.hidden_columns.map((h) => (
          <div key={h.column} style={{ fontSize: '12px', color: '#cfd2d6', padding: '2px 0' }}>
            <span style={S.mono}>{h.column}</span>
            <span style={{ color: '#889' }}> — {h.why}</span>
          </div>
        ))}
      </Section>
    </div>
  );
}
